import React from 'react';
import PropTypes from 'prop-types';
import T from 'i18n-react';
import kebabCase from 'lodash/kebabCase';

import { CalculatorIcon, IdIcon, MoneyStackIcon, PaperIcon, VehicleLorryIcon } from '@motorway/mw-highway-code';

import { getMarkAsDeliveredAttributes } from 'Components/Account/Purchases/Purchases.helpers';
import cypressIds from 'Components/cypress_ids.json';
import MenuList, { MENU_LINK_TYPE } from 'Components/shared/MenuList/MenuList';
import { useVehicle } from 'Context/VehicleContext';
import { useAccountPermitsChecker } from 'Controllers/AccountPermitsChecker/AccountPermitsChecker.helpers';
import { useIsPurchaseSummaryAllowed } from 'Pages/purchase-summary/helpers/isPurchaseSummaryAllowed.helpers';
import {
	triggerCompletePurchasePaymentMenuClickedEvent,
	triggerCompletePurchasesCancellationMenuClickedEvent,
	triggerCompletePurchasesDeliveryDetailsMenuClickedEvent,
	triggerCompletePurchasesMarkAsDeliveredEvent,
	triggerCompletePurchasesSellerDetailsMenuClickedEvent,
	triggerCompletePurchasesUnmarkAsDeliveredEvent,
	triggerPurchasesCompletePurchaseSummaryButtonClickedEvent,
} from 'Services/analytics/events';
import { isE2ETestEnv } from 'Utilities/helpers';
import useCheckIsMobilizePartner, { checkIsMobilizeEnquiry } from 'Utilities/hooks/useIsMobilizeDealer';
import usePaymentsMVPEnabled from 'Utilities/hooks/usePaymentsMVPEnabled';
import usePaymentsPermission from 'Utilities/hooks/usePaymentsPermission';
import { useZendesk } from 'Utilities/hooks/useZendesk';
import ROUTES from 'Utilities/routes';
import { SOLD_CATEGORY } from 'Utilities/vehicleState';

T.setTexts(require('Components/texts.json'));

const MoreMenuView = ({ dismissModal, enquiryId, onMutateDeliveryStatus }) => {
	const { enquirySoldDate, isDelivered, prettyVrm, sourcePartner, state, userBid } = useVehicle();

	const permits = useAccountPermitsChecker();
	const isStateSoldDocumentReview = state?.slug === SOLD_CATEGORY.SOLD_DOCUMENTS_REVIEWED;

	const isPaymentsMVPEnabled = usePaymentsMVPEnabled();
	const hasPaymentsPermission = usePaymentsPermission();
	const isMobilizeDealer = useCheckIsMobilizePartner();
	const vehiclePaymentMenuItemLinkType = isE2ETestEnv() ? MENU_LINK_TYPE.ROUTE : MENU_LINK_TYPE.LINK;
	const showAdjustmentsAndCancellationsMenuItem = permits.displayMoreMenu();
	const showVehiclePaymentMenuItem =
		isStateSoldDocumentReview &&
		isPaymentsMVPEnabled &&
		hasPaymentsPermission &&
		!(isMobilizeDealer && checkIsMobilizeEnquiry(sourcePartner));
	const { getPrePopulatedAdjustmentsAndCancellationLink } = useZendesk();

	const adjustmentsAndCancellationLink = getPrePopulatedAdjustmentsAndCancellationLink({ vrm: prettyVrm });
	const shouldShowPurchaseSummary = useIsPurchaseSummaryAllowed({ soldDate: enquirySoldDate, state });
	const items = [
		...(shouldShowPurchaseSummary
			? [
					{
						cypressId: cypressIds.buttons.purchasePack,
						href: ROUTES.PURCHASE_SUMMARY.as(enquiryId),
						icon: PaperIcon,
						id: kebabCase(ROUTES.PURCHASE_SUMMARY.as(enquiryId)),
						onClick: () => {
							triggerPurchasesCompletePurchaseSummaryButtonClickedEvent();
						},
						text: T.translate('account.moreMenu.purchaseSummary'),
					},
				]
			: []),
		{
			href: ROUTES.SELLER_DETAILS.as(enquiryId),
			icon: IdIcon,
			id: kebabCase(ROUTES.SELLER_DETAILS.as(enquiryId)),
			onClick: () => {
				triggerCompletePurchasesSellerDetailsMenuClickedEvent({ id: enquiryId });
			},
			text: T.translate('account.moreMenu.sellerDetails'),
		},
		{
			href: ROUTES.DELIVERY.as(enquiryId),
			icon: VehicleLorryIcon,
			id: kebabCase(ROUTES.DELIVERY.as(enquiryId)),
			onClick: () => {
				triggerCompletePurchasesDeliveryDetailsMenuClickedEvent({ id: enquiryId });
			},
			text: T.translate('account.moreMenu.deliveryDetails'),
		},
		...(showVehiclePaymentMenuItem
			? [
					{
						href: ROUTES.VEHICLE_PAYMENT.as(enquiryId),
						icon: MoneyStackIcon,
						id: kebabCase(ROUTES.VEHICLE_PAYMENT.as(enquiryId)),
						isProtected: true,
						linkType: vehiclePaymentMenuItemLinkType,
						onClick: (e) => {
							triggerCompletePurchasePaymentMenuClickedEvent({ id: enquiryId });
							/**
							 * Cypress doesn't work SSR, which we need to force
							 * for payments flow
							 */
							if (!isE2ETestEnv()) {
								e.preventDefault();
								window.location.href = ROUTES.VEHICLE_PAYMENT.as(enquiryId);
							}
						},
						text: T.translate('account.moreMenu.vehiclepayment'),
					},
				]
			: []),
		...(showAdjustmentsAndCancellationsMenuItem
			? [
					{
						cypressId: cypressIds.buttons.purchasesCompleteAdjustmentsAndCancellations,
						href: adjustmentsAndCancellationLink,
						icon: CalculatorIcon,
						id: `adjustmentsAndCalculations-${enquiryId}`,
						linkType: MENU_LINK_TYPE.LINK,
						onClick: () => {
							triggerCompletePurchasesCancellationMenuClickedEvent({ id: enquiryId });

							/* Unique window name means that the browser always opens a new window
							 * instead of reusing the same window if window.open is called multiple
							 * times with the same URL.
							 */
							const uniqueWindowName = new Date().getTime().toString();
							window.open(adjustmentsAndCancellationLink, uniqueWindowName);
						},
						text: T.translate('account.moreMenu.adjustmentsAndCancellations'),
					},
				]
			: []),
		{
			...getMarkAsDeliveredAttributes({ id: enquiryId, isDelivered }),
			linkType: 'button',
			onClick: () => {
				const deliveredStatus = !isDelivered;
				onMutateDeliveryStatus(deliveredStatus, userBid?.id);

				if (isDelivered) {
					triggerCompletePurchasesUnmarkAsDeliveredEvent();
				} else {
					triggerCompletePurchasesMarkAsDeliveredEvent();
				}
				dismissModal?.();
			},
		},
	];

	return <MenuList items={items} />;
};

MoreMenuView.propTypes = {
	dismissModal: PropTypes.func,
	enquiryId: PropTypes.number.isRequired,
	onMutateDeliveryStatus: PropTypes.func,
};

export default MoreMenuView;
